import PasswordValidator from 'password-validator';
import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { centralAPI } from '../services/central-api';
import { translate } from '../services/translate';
import { ClientError } from '../services/central-api/base-service';
import Container from '../components/Container';
import PasswordInput from '../components/PasswordInput';
import Card from '../components/Card';
import MyAppText from '../components/MyAppText';
import Header from '../components/Header';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import CustomButton from '../components/CustomButton';
import { WindowInformation } from '../services/window-information';
import { forceRedirectToLogin } from '../services/navigation-assistant';

export default function RotatePassword() {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();


    const [isFormValid, setIsFormValid] = useState(false);
    const [isValidMin, setIsValidMin] = useState(false);
    const [isValidDigits, setIsValidDigits] = useState(false);
    const [isValidUppercase, setIsValidUppercase] = useState(false);
    const [isValidLowercase, setIsValidLowercase] = useState(false);

    const validator = new PasswordValidator();
    validator.is().min(8).has().uppercase().has().lowercase().has().digits(2);

    const [oldPassword, setOldPassword] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    useEffect(() => {
        if (oldPassword === '' || password === '' || confirmPassword === '') {
            return setIsFormValid(false);
        }

        if (confirmPassword !== password && (oldPassword !== '' || password !== '' || confirmPassword !== '')) {
            return setIsFormValid(false);
        }

        if (!validator.validate(password)) {
            return setIsFormValid(false);
        }

        return setIsFormValid(true);
    }, [oldPassword, password, confirmPassword]);

    useEffect(() => {
        const missingValidations = validator.validate(password, { list: true }) as string[];
        setIsValidMin(!missingValidations.includes('min'));
        setIsValidUppercase(!missingValidations.includes('uppercase'));
        setIsValidLowercase(!missingValidations.includes('lowercase'));
        setIsValidDigits(!missingValidations.includes('digits'));
    }, [password]);

    async function save() {
        try {
            await centralAPI.updateOwnUser({
                oldPassword,
                password,
                confirmPassword
            });
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            forceRedirectToLogin();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (
        <Container>
            <Header title={translate('updatePassword')} />
            <Card scrollView={true}>
                <View style={styles.pageCenter(windowInfo.isMobile)}>
                    <View style={styles.container(windowInfo.isMobile)}>
                        <View style={{ gap: 16 }}>
                            <MyAppText style={styles.attentionText}>{translate('attention')}!</MyAppText>
                            <MyAppText style={styles.attentionSubText}>{translate('rotatePasswordExplanation')}</MyAppText>
                        </View>
                        <View style={styles.subTitleContainer}>
                            <MyAppText style={styles.detailsFont}>{translate('securityPassword')}</MyAppText>
                        </View>
                        <PasswordInput
                            label={translate('oldPassword')}
                            placeholder=''
                            value={oldPassword}
                            onChangeText={setOldPassword}
                            invalid={() => false}
                            required={true}
                            viewStyle={{ maxWidth: 250 }}
                        />
                        <View style={styles.passwordInputContainer(windowInfo.isMobile)}>
                            <PasswordInput
                                label={translate('newPassword')}
                                placeholder=''
                                value={password}
                                onChangeText={setPassword}
                                invalid={() => false}
                                required={true}
                                disabled={!oldPassword}
                                viewStyle={{ maxWidth: 250 }}
                            />
                            <PasswordInput
                                label={translate('confirmNewPassword')}
                                placeholder=''
                                value={confirmPassword}
                                onChangeText={setConfirmPassword}
                                invalid={() => password != confirmPassword}
                                errorMessage={translate('passwordsNotEqual')}
                                required={true}
                                disabled={!oldPassword}
                                viewStyle={{ maxWidth: 250 }}
                            />
                        </View>
                        <View style={{ gap: 4 }}>
                            <View style={styles.requirementContainer}>
                                <FontAwesomeIcon icon={faCircleCheck as IconDefinition} fontSize={16} style={password === '' ? styles.waiting : isValidMin ? styles.valid : styles.invalid} />
                                <MyAppText style={[styles.label, password === '' ? null : isValidMin ? styles.valid : styles.invalid]}>{translate('atLeast8Characters')}</MyAppText>
                            </View>
                            <View style={styles.requirementContainer}>
                                <FontAwesomeIcon icon={faCircleCheck as IconDefinition} fontSize={16} style={password === '' ? styles.waiting : isValidDigits ? styles.valid : styles.invalid} />
                                <MyAppText style={[styles.label, password === '' ? null : isValidDigits ? styles.valid : styles.invalid]}>{translate('atLeast2Digits')}</MyAppText>
                            </View>
                            <View style={styles.requirementContainer}>
                                <FontAwesomeIcon icon={faCircleCheck as IconDefinition} fontSize={16} style={password === '' ? styles.waiting : isValidUppercase ? styles.valid : styles.invalid} />
                                <MyAppText style={[styles.label, password === '' ? null : isValidUppercase ? styles.valid : styles.invalid]}>{translate('atLeastOneUppercase')}</MyAppText>
                            </View>
                            <View style={styles.requirementContainer}>
                                <FontAwesomeIcon icon={faCircleCheck as IconDefinition} fontSize={16} style={password === '' ? styles.waiting : isValidLowercase ? styles.valid : styles.invalid} />
                                <MyAppText style={[styles.label, password === '' ? null : isValidLowercase ? styles.valid : styles.invalid]}>{translate('atLeastOneLowercase')}</MyAppText>
                            </View>
                        </View>
                        <View style={{ alignItems: 'flex-end' }}>
                            <CustomButton
                                onPress={() => save()}
                                text={translate('saveChanges')}
                                disabled={!isFormValid}
                            />
                        </View>
                    </View>
                </View>
            </Card>
        </Container>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: (isMobile: boolean) => ({
        gap: 16,
        maxWidth: isMobile ? undefined : 700
    }),
    pageCenter: (isMobile: boolean) => ({
        justifyContent: 'center',
        alignItems: isMobile ? undefined : 'center'
    }),
    passwordInputContainer: (isMobile: boolean) => ({
        gap: 16,
        flexDirection: isMobile ? 'column' : 'row'
    }),
    attentionText: {
        fontSize: 22,
        color: theme.colors.neutral['500'],
        fontWeight: '700',
    },
    attentionSubText: {
        fontSize: 16,
        fontWeight: '400',
        color: theme.colors.neutral['400'],
    },
    detailsFont: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.primary['500']
    },
    subTitleContainer: {
        borderBottomWidth: 1,
        borderColor: theme.colors.primary['200'],
        paddingBottom: 8
    },
    requirementContainer: {
        flexDirection: 'row',
        gap: 8,
        alignItems: 'center'
    },
    label: {
        fontSize: 14,
        fontWeight: '500',
        color: theme.colors.neutral['400'],
    },
    waiting: {
        color: theme.colors.neutral['400'],
    },
    valid: {
        color: theme.colors.successes['300'],
    },
    invalid: {
        color: theme.colors.errors['300'],
    }
}));
